import axios from 'axios'
import { APIWHATSAPP } from '@/API.js'

const apiClient = axios.create({
  baseURL: `${APIWHATSAPP}/api`,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
})

// Interceptor para inyectar dinámicamente el token actual en cada petición
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('argusblack.token') || localStorage.getItem('auth_token') || '';
  if (token) {
    config.headers['Authorization'] = token;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Actualización del token de autorización
document.addEventListener('actualizar:token', function(e) {
  apiClient.defaults.headers['Authorization'] = e.detail
})

// Eliminación del token de autorización
document.addEventListener('eliminar:token', function() {
  delete apiClient.defaults.headers['Authorization']
})

export default {
  /**
   * Obtiene el listado de conversaciones y actualiza la caché local
   * @param {Object} params
   */
  conversaciones(params = {}) {
    const queryParams = {
      archivado: 0,
      ...params
    };
    const esArchivado = Number(queryParams.archivado) === 1;
    const cacheKey = esArchivado
      ? 'argusblack.whatsapp.conversaciones_archivadas'
      : 'argusblack.whatsapp.conversaciones';

    return apiClient.get('/whatsapp/conversaciones', { params: queryParams }).then((response) => {
      // Guardar en caché si no es una búsqueda con filtros y es la primera página o listado base
      if (!queryParams.busqueda && !queryParams.search && (!queryParams.pagina || Number(queryParams.pagina) === 1)) {
        const listado = Array.isArray(response.data)
          ? response.data
          : (response.data && response.data.data ? response.data.data : (response.data && response.data.conversaciones ? response.data.conversaciones : []));
        if (Array.isArray(listado)) {
          try {
            localStorage.setItem(cacheKey, JSON.stringify(listado));
          } catch (e) {}
        }
      }
      return response;
    });
  },

  /**
   * Obtiene el listado de conversaciones recientes (archivado = 0)
   * @param {Object} params
   */
  conversacionesRecientes(params = {}) {
    return this.conversaciones({ ...params, archivado: 0 });
  },

  /**
   * Obtiene el listado de conversaciones archivadas (archivado = 1)
   * @param {Object} params
   */
  conversacionesArchivadas(params = {}) {
    return this.conversaciones({ ...params, archivado: 1 });
  },

  /**
   * Obtiene el conteo de mensajes nuevos sin leer (fecha_lectura = null), máximo 99
   */
  mensajesNuevos() {
    return apiClient.get('/whatsapp/mensajes/nuevos');
  },

  /**
   * Obtiene los mensajes de una conversación y actualiza la caché local (LocalStorage)
   * @param {Number|String} id_telefono_cliente
   * @param {Object} config
   */
  mensajes(id_telefono_cliente, config = {}) {
    const opciones = config.params !== undefined || config.signal !== undefined
      ? config
      : { params: config };
    return apiClient.get(`/whatsapp/telefonos/${id_telefono_cliente}/mensajes`, opciones).then((response) => {
      const res = response && response.data !== undefined ? response.data : response;
      let listado = [];
      if (Array.isArray(res)) listado = res;
      else if (res && Array.isArray(res.data)) listado = res.data;
      else if (res && Array.isArray(res.mensajes)) listado = res.mensajes;

      if (id_telefono_cliente && Array.isArray(listado) && listado.length > 0) {
        try {
          const key = `argusblack.whatsapp.chat_${id_telefono_cliente}`;
          const raw = localStorage.getItem(key);
          let prev = {};
          if (raw) {
            try { prev = JSON.parse(raw); } catch (e) {}
          }
          const msgs = listado.slice(-150);
          const actualizado = {
            ...prev,
            id: id_telefono_cliente,
            mensajes: msgs,
            total_mensajes: msgs.length,
            ultima_actualizacion: new Date().toISOString()
          };
          localStorage.setItem(key, JSON.stringify(actualizado));
        } catch (e) {}
      }
      return response;
    });
  },

  /**
   * Envía un mensaje de texto por WhatsApp a través de la API
   * POST /whatsapp/telefonos/{id_telefono_cliente}/enviar/mensaje
   * Body: { "mensaje": "..." }
   * @param {Number|String} id_telefono_cliente
   * @param {Object|String} payload
   */
  enviarMensaje(id_telefono_cliente, payload) {
    if (typeof FormData !== 'undefined' && payload instanceof FormData) {
      return apiClient.post(`/whatsapp/telefonos/${id_telefono_cliente}/enviar/mensaje`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    const data = typeof payload === 'string' ? { mensaje: payload } : payload;
    return apiClient.post(`/whatsapp/telefonos/${id_telefono_cliente}/enviar/mensaje`, data);
  },

  /**
   * Inicializa una conversación enviando la plantilla oficial de inicio de conversación
   * POST /whatsapp/telefonos/{id_telefono_cliente}/inicializar
   * @param {Number|String} id_telefono_cliente
   * @param {Object} payload
   */
  inicializarConversacion(id_telefono_cliente, payload = {}) {
    return apiClient.post(`/whatsapp/telefonos/${id_telefono_cliente}/inicializar`, payload);
  },

  /**
   * Inicializa una nueva conversación con un número nuevo o existente
   * POST /whatsapp/inicializar-nuevo
   * @param {Object} data { numero, prefijo, id_cliente, enviar_plantilla }
   */
  inicializarNuevoContacto(data = {}) {
    return apiClient.post('/whatsapp/inicializar-nuevo', data);
  },

  /**
   * Actualiza el registro de teléfono cliente (campos: chat, chateando, archivado)
   * PUT /whatsapp/telefonos/{id_telefono}
   * Body: { "chateando": 0 | 1, "chat": 0 | 1, "archivado": 0 | 1 }
   * @param {Number|String} id_telefono
   * @param {Object|Boolean|Number} payload
   */
  actualizarTelefonoCliente(id_telefono, payload = {}) {
    const data = (typeof payload === 'number' || typeof payload === 'boolean')
      ? { chateando: payload ? 1 : 0 }
      : payload;
    return apiClient.put(`/whatsapp/telefonos/${id_telefono}`, data);
  },

  actualizarChateando(id_telefono, payload = {}) {
    return this.actualizarTelefonoCliente(id_telefono, payload);
  },

  /**
   * Asigna un cliente al número de teléfono seleccionado
   * PUT /whatsapp/telefonos/{id_telefono}/asignar-cliente
   * Body: { id_cliente: number }
   * @param {Number|String} id_telefono
   * @param {Number|String} id_cliente
   */
  asignarCliente(id_telefono, id_cliente) {
    return apiClient.put(`/whatsapp/telefonos/${id_telefono}/asignar-cliente`, { id_cliente });
  },

  /**
   * Marca el chat como no leído (establece en null fecha_lectura del último mensaje entrante)
   * POST /whatsapp/telefonos/{id_telefono}/marcar-no-leido
   * @param {Number|String} id_telefono
   */
  marcarComoNoLeido(id_telefono) {
    return apiClient.post(`/whatsapp/telefonos/${id_telefono}/marcar-no-leido`);
  },

  /**
   * Bloquea a un usuario de WhatsApp (Meta Graph API) y marca bloqueado = 1 en la base de datos
   * POST /whatsapp/telefonos/{id_telefono}/bloquear
   * @param {Number|String} id_telefono
   */
  bloquearTelefonoCliente(id_telefono) {
    return apiClient.post(`/whatsapp/telefonos/${id_telefono}/bloquear`);
  },

  /**
   * Desbloquea a un usuario de WhatsApp (Meta Graph API) y marca bloqueado = 0 en la base de datos
   * POST /whatsapp/telefonos/{id_telefono}/desbloquear
   * @param {Number|String} id_telefono
   */
  desbloquearTelefonoCliente(id_telefono) {
    return apiClient.post(`/whatsapp/telefonos/${id_telefono}/desbloquear`);
  },

  /**
   * Elimina el registro del teléfono de la tabla telefonos_clientes
   * DELETE /whatsapp/telefonos/{id_telefono}
   * @param {Number|String} id_telefono
   */
  eliminarTelefonoCliente(id_telefono) {
    return apiClient.delete(`/whatsapp/telefonos/${id_telefono}`).then((response) => {
      this.limpiarCacheChat(id_telefono);
      return response;
    });
  },

  /**
   * Vacía todos los mensajes de una conversación y purga su caché
   * DELETE /whatsapp/telefonos/{id_telefono}/mensajes
   * @param {Number|String} id_telefono
   */
  vaciarChat(id_telefono) {
    return apiClient.delete(`/whatsapp/telefonos/${id_telefono}/mensajes`).then((response) => {
      this.limpiarCacheChat(id_telefono);
      return response;
    });
  },

  /**
   * Obtiene la vista previa Open Graph de un enlace externo
   * GET /whatsapp/preview-url?url=...
   * @param {String} url
   */
  previewUrl(url) {
    if (!url) return Promise.reject(new Error('URL requerida'));
    return apiClient.get('/whatsapp/preview-url', { params: { url } });
  },

  /**
   * Helpers síncronos de caché LocalStorage (0ms)
   */
  obtenerCacheConversaciones() {
    try {
      const raw = localStorage.getItem('argusblack.whatsapp.conversaciones');
      if (raw) {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (e) {}
    return [];
  },

  obtenerCacheMensajes(idTelefonoONumero) {
    if (!idTelefonoONumero) return [];
    try {
      const raw = localStorage.getItem(`argusblack.whatsapp.chat_${idTelefonoONumero}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.mensajes)) {
          return parsed.mensajes;
        }
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (e) {}
    return [];
  },

  limpiarCacheChat(idTelefonoONumero) {
    if (!idTelefonoONumero) return;
    try {
      localStorage.removeItem(`argusblack.whatsapp.chat_${idTelefonoONumero}`);
    } catch (e) {}
  }
};

